import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteProductChildrenApi,
  getAllProductChildrenApi,
  getProductChildrenApi,
  postProductChildrenApi,
  updateProductChildrenApi,
} from "../../api/ecommerce/ProductChildrenApi";

const initialState = {
  productChild: null,
  productChildren: [],
  loading: false,
  detailsLoading: false,
  saving: false,
  deleting: false,
  error: null,
  success: false,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.data?.message ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

export const postProductChildren = createAsyncThunk(
  "productChildren/postProductChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postProductChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const updateProductChildren = createAsyncThunk(
  "productChildren/updateProductChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateProductChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const deleteProductChildren = createAsyncThunk(
  "productChildren/deleteProductChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteProductChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getProductChildren = createAsyncThunk(
  "productChildren/getProductChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getProductChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getAllProductChildren = createAsyncThunk(
  "productChildren/getAllProductChildren",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getAllProductChildrenApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

const productChildrenSlice = createSlice({
  name: "productChildren",
  initialState,
  reducers: {
    clearProductChildrenError: (state) => {
      state.error = null;
    },
    clearProductChildrenState: (state) => {
      state.productChild = null;
      state.productChildren = [];
      state.loading = false;
      state.detailsLoading = false;
      state.saving = false;
      state.deleting = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductChildren.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getProductChildren.fulfilled, (state, action) => {
        state.productChild = action?.payload?.response ?? action?.payload;
        state.detailsLoading = false;
        state.success = true;
      })
      .addCase(getProductChildren.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(getAllProductChildren.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getAllProductChildren.fulfilled, (state, action) => {
        state.productChildren = action?.payload?.response ?? action?.payload ?? [];
        state.loading = false;
        state.success = true;
      })
      .addCase(getAllProductChildren.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(postProductChildren.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(postProductChildren.fulfilled, (state, action) => {
        state.productChild = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(postProductChildren.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(updateProductChildren.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateProductChildren.fulfilled, (state, action) => {
        state.productChild = action?.payload?.response ?? action?.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(updateProductChildren.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      })
      .addCase(deleteProductChildren.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteProductChildren.fulfilled, (state) => {
        state.deleting = false;
        state.success = true;
      })
      .addCase(deleteProductChildren.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || null;
        state.success = false;
      });
  },
});

export const { clearProductChildrenError, clearProductChildrenState } =
  productChildrenSlice.actions;

export default productChildrenSlice.reducer;
